import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  FormField,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import React, { useState } from 'react';
import { FieldPathByValue, FieldValues } from 'react-hook-form';
import PhoneInput, {
  Props,
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en.json';

import { FieldProps } from './shared';

const CountrySelect = ({
  value,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Select> & {
  value: Props<unknown>['country'];
  className?: string;
}) => {
  // Almost every visitor is calling from India and never touches this
  // field, but Radix's Select always mirrors every <SelectItem> into a
  // hidden native <select> (for form/autofill semantics) regardless of
  // whether the popover has ever been opened — so all 245 countries were
  // landing in the DOM on every page load. Rendering just the current
  // value until the user actually opens the picker keeps that hidden
  // select at one option for the common case, and expands to the full
  // list the moment they interact with it (before they can pick anything
  // else, so the hidden select always has an option for whatever they
  // choose).
  const [hasOpened, setHasOpened] = useState(false);
  const countries = hasOpened ? getCountries() : [value!];

  return (
    <Select
      {...props}
      value={value}
      onOpenChange={(open) => {
        if (open) setHasOpened(true);
        props.onOpenChange?.(open);
      }}
    >
      {/* role="combobox" doesn't take its name from content (the flag <img>'s
          alt isn't enough), so it needs an explicit aria-label. */}
      <SelectTrigger
        className={cn(className)}
        aria-label={`Country code: ${(en as Record<string, string>)[value!]} +${getCountryCallingCode(value!)}`}
      >
        <img
          className="shrink-0 h-full aspect-auto pointer-events-none"
          src={`/storage/country-flags/3x2/${value}.svg`}
          alt={(en as Record<string, string>)[value!]}
          width={30}
          height={20}
        />
      </SelectTrigger>
      <SelectContent>
        {countries.map((country) => (
          <SelectItem key={country} value={country}>
            {en[country]} +{getCountryCallingCode(country)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export function PhoneNoField<
  T extends FieldValues,
  P extends FieldPathByValue<T, Props<unknown>['value']>,
>({
  control,
  name,
  rules,
  label,
  description,
  className,
  ...props
}: FieldProps<T, P> &
  Omit<
    React.ComponentPropsWithoutRef<typeof Input>,
    'defaultValue' | 'value'
  >) {
  const [country, setCountry] = useState<Props<unknown>['country']>('IN');

  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field: { value, onChange, ...field } }) => (
        <FormItem className="grow">
          <FormLabel>{label}</FormLabel>
          {/* w-full so the row is bounded by the field rather than by its
              own content: an <input> is a flex item with min-width:auto, so
              without that (and the min-w-0 on Input) it holds its intrinsic
              ~20ch width and pushes the row past the viewport on a phone.

              FormControl wraps only the phone <input>, not this whole row —
              it's what stamps the id the FormLabel's htmlFor points at, and
              that has to land on the actual form control, not a layout div,
              or the label associates with nothing. */}
          <div className="flex w-full items-center">
            <CountrySelect
              name="country_code"
              className={cn('max-w-fit h-10', className)}
              value={country}
              onValueChange={(code) => {
                onChange('');
                setCountry(code as Props<unknown>['country']);
              }}
            />
            <FormControl>
              <PhoneInput
                country={country}
                withCountryCallingCode
                inputComponent={Input}
                international
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                onChange={onChange as any}
                {...props}
                {...field}
              />
            </FormControl>
          </div>
          <FormDescription>{description?.(value)}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
